// Catalog.ID Client — the ceremony console's ledger context
// ============================================================================
// docs/cid-governance.md M6, D6, D8, D20. What this node's ledger store says
// about the operator codes a ceremony document names, read before anything is
// signed:
//
//   • `GET /api/federation/operators/{code}` — the operator record (reserved,
//     bound, released, retired), the binding's organization and key, and its
//     sequence; 404 when the ledger never named the code (not reserved);
//   • `GET /api/account/publickeys/{cid}` — the signer's current
//     `main_signing_key` as the node publishes it.
//
// From these the console shows the binding's organization key beside the
// current one (D6) and refuses what the Developer's `cl-root` would refuse
// (LedgerState, CertificateRequests.Check): an unreserved or retired code, a
// binding consent that is not the next sequence, a request or bulk consent for
// a code bound to another organization, a bulk or organization migration
// consent under a key its binding does not carry. A countersignature under a
// changed key is signed with a warning, because its new binding travels in
// the same ceremony and is signed first.
//
// Every code is served, not only this node's own: an operator without a CID
// node signs its ceremony at another operator's node.

import { formatFingerprint, isOrganizationCid, publicKeyFingerprint } from '@catalog-federation';
import { api, ApiError } from '../api/client';
import * as aes from '../crypto/aes';
import type { CeremonyDocument, RenderedKey } from './payload';

/** One row of the node's `federationoperator` projection (D6). */
export interface OperatorRecord {
    code: string;
    /** `reserved`, `bound`, `released` or `retired`. */
    status: string;
    orgCid: string | null;
    orgPublicKey: Uint8Array | null;
    /** The operator record's sequence; a binding consent names `sequence + 1`. */
    sequence: number;
    ledgerSeq: number;
}

const OPERATOR_STATUSES = new Set(['reserved', 'bound', 'released', 'retired']);

/** Reads the endpoint's JSON; throws on anything else. */
export function parseOperatorRecord(json: unknown): OperatorRecord {
    const o = json as Record<string, unknown> | null;
    if (o === null || typeof o !== 'object'
        || typeof o['code'] !== 'string'
        || typeof o['status'] !== 'string' || !OPERATOR_STATUSES.has(o['status'])
        || !Number.isSafeInteger(o['sequence']) || !Number.isSafeInteger(o['ledgerSeq'])
        || !(o['orgCid'] === null || typeof o['orgCid'] === 'string')
        || !(o['orgPublicKey'] === null || typeof o['orgPublicKey'] === 'string')) {
        throw new Error('the node answered an operator record this console cannot read');
    }
    return {
        code: o['code'],
        status: o['status'],
        orgCid: o['orgCid'] as string | null,
        orgPublicKey: o['orgPublicKey'] === null ? null : aes.fromBase64(o['orgPublicKey'] as string),
        sequence: o['sequence'] as number,
        ledgerSeq: o['ledgerSeq'] as number,
    };
}

/** The operator record of `code`, or null when this node's ledger never named it. */
export async function fetchOperatorRecord(code: string): Promise<OperatorRecord | null> {
    try {
        return parseOperatorRecord(await api.get(`/api/federation/operators/${encodeURIComponent(code)}`));
    } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
    }
}

/** `cid`'s current `main_signing_key` as the node's directory publishes it
 *  (the highest keyIndex), or null when it publishes none. */
export async function fetchCurrentSigningKey(cid: string): Promise<Uint8Array | null> {
    const keys = await api.get<{ keyId: string; publicKey: string; keyIndex: number }[]>(
        `/api/account/publickeys/${encodeURIComponent(cid)}`);
    let best: { publicKey: string; keyIndex: number } | null = null;
    for (const k of keys) {
        if (k.keyId === 'main_signing_key' && (best === null || k.keyIndex > best.keyIndex)) best = k;
    }
    return best === null ? null : aes.fromBase64(best.publicKey);
}

/** The operator code this node serves (`/health` `node.code`), or null when it does not say. */
export async function fetchNodeCode(): Promise<string | null> {
    const health = await api.get<{ node?: { code?: unknown } }>('/health');
    const code = health?.node?.code;
    return typeof code === 'string' && code.length > 0 ? code : null;
}

// ── facts ───────────────────────────────────────────────────────────────

export interface LedgerFacts {
    /** The organization or account whose key signs. */
    signerCid: string;
    /** Its current `main_signing_key` as the node publishes it. */
    currentKey: Uint8Array | null;
    /** The operator records of the codes the document names; null = not reserved. */
    records: Readonly<Record<string, OperatorRecord | null>>;
    /** Codes bound to the signing organization (`operatorCodes`), for its own migration consent. */
    boundCodes: readonly string[];
}

/** The operator codes whose records the console reads for `doc`. */
export function codesNamedBy(doc: CeremonyDocument, boundCodes: readonly string[]): string[] {
    const codes = new Set<string>();
    switch (doc.kind) {
        case 'certificate_countersignature':
            codes.add(doc.request.request.operatorId);
            break;
        case 'binding_consent':
            codes.add(doc.consent.operatorId);
            break;
        case 'bulk_migration_consent':
            codes.add(doc.consent.fromOperatorId).add(doc.consent.toOperatorId);
            break;
        case 'account_migration_consent':
            codes.add(doc.consent.fromOperatorId).add(doc.consent.toOperatorId);
            if (isOrganizationCid(doc.consent.accountCid)) for (const c of boundCodes) codes.add(c);
            break;
    }
    return [...codes].sort();
}

/** Reads what the node says about `doc` for the signer `signerCid`. */
export async function gatherLedgerFacts(
    doc: CeremonyDocument, signerCid: string, boundCodes: readonly string[],
): Promise<LedgerFacts> {
    const codes = codesNamedBy(doc, boundCodes);
    const [currentKey, ...records] = await Promise.all([
        fetchCurrentSigningKey(signerCid),
        ...codes.map(c => fetchOperatorRecord(c)),
    ]);
    const byCode: Record<string, OperatorRecord | null> = {};
    codes.forEach((c, i) => { byCode[c] = records[i] as OperatorRecord | null; });
    return { signerCid, currentKey: currentKey as Uint8Array | null, records: byCode, boundCodes };
}

// ── judgement ───────────────────────────────────────────────────────────

export interface LedgerJudgement {
    /** Why the document must not be signed; empty when it may. */
    blocking: string[];
    /** What the signer should know. */
    notes: string[];
    /** The signer's current key, then the key of each binding that matters (D6). */
    keys: RenderedKey[];
}

/** Judges `doc` against what the node's ledger says (see the file header). */
export async function judgeLedgerContext(doc: CeremonyDocument, facts: LedgerFacts): Promise<LedgerJudgement> {
    const j: LedgerJudgement = { blocking: [], notes: [], keys: [] };
    const signer = facts.signerCid;
    const record = (code: string): OperatorRecord | null => facts.records[code] ?? null;

    if (facts.currentKey === null) {
        j.blocking.push(`This node publishes no signing key (main_signing_key) for ${signer}, so the key that would `
            + 'sign cannot be checked. Nothing can be signed.');
    } else {
        j.keys.push({ label: `${signer} — signing key now`, fingerprint: await fingerprint(facts.currentKey) });
    }
    const showBinding = async (r: OperatorRecord): Promise<void> => {
        if (r.status === 'bound' && r.orgPublicKey !== null) {
            j.keys.push({
                label: `key in ${r.code}'s binding`,
                detail: `${r.orgCid}, operator record sequence ${r.sequence}, ledger record ${r.ledgerSeq}`,
                fingerprint: await fingerprint(r.orgPublicKey),
            });
        }
    };
    const isCurrent = (key: Uint8Array | null): boolean =>
        key !== null && facts.currentKey !== null && sameBytes(key, facts.currentKey);
    const requireOpen = (code: string, role: string): OperatorRecord | null => {
        const r = record(code);
        if (r === null) {
            j.blocking.push(`Operator ${code} (${role}) has no operator record in this node's ledger: the code is not `
                + 'reserved. The Developer reserves a code before anything else is signed for it.');
            return null;
        }
        if (r.status === 'retired') {
            j.blocking.push(`Operator ${code} (${role}) is retired; its record is closed and nothing more is signed for it.`);
            return null;
        }
        return r;
    };
    const requireCurrentEmbedded = (key: Uint8Array): void => {
        if (facts.currentKey !== null && !isCurrent(key)) {
            j.blocking.push(`The document names a key that is not ${signer}'s current signing key, so the Developer `
                + 'would check it against the wrong key. Prepare it again.');
        }
    };

    switch (doc.kind) {
        case 'certificate_countersignature': {
            const op = doc.request.request.operatorId;
            const r = requireOpen(op, 'the node\'s operator');
            if (r === null) break;
            if (r.status === 'bound' && r.orgCid !== signer) {
                j.blocking.push(`Operator ${op} is bound to organization ${r.orgCid}. The Developer accepts only `
                    + `${r.orgCid}'s countersignature for ${op}'s nodes, not ${signer}'s.`);
                break;
            }
            if (r.status === 'bound') {
                await showBinding(r);
                if (isCurrent(r.orgPublicKey)) {
                    j.notes.push(`${op}'s binding carries ${signer}'s current key: the Developer checks this `
                        + 'countersignature against it.');
                } else {
                    j.notes.push(`${op}'s binding carries an older key of ${signer} (D6). The Developer refuses this `
                        + `countersignature until a binding at sequence ${r.sequence + 1} carries the current key: `
                        + `sign a binding consent for ${op} as well and send both files; the binding is signed first.`);
                }
            } else {
                j.notes.push(`Operator ${op} is not bound yet. Sign the binding consent for ${op} as well and send `
                    + 'both files: the Developer signs the binding before the certificate (D8).');
            }
            break;
        }

        case 'binding_consent': {
            const c = doc.consent;
            requireCurrentEmbedded(c.orgPublicKey);
            const r = requireOpen(c.operatorId, 'the code to bind');
            if (r === null) break;
            if (c.sequence !== r.sequence + 1) {
                j.blocking.push(`This consent is for sequence ${c.sequence} of ${c.operatorId}'s operator record, `
                    + `but the next is ${r.sequence + 1}. The Developer would refuse it; prepare the consent again.`);
            }
            await showBinding(r);
            if (r.status === 'bound' && r.orgCid !== c.orgCid) {
                j.notes.push(`${c.operatorId} is bound to ${r.orgCid} today; this binding replaces that binding.`);
            } else if (r.status === 'bound' && isCurrent(r.orgPublicKey)) {
                j.notes.push(`${c.operatorId} is already bound to ${c.orgCid} with this key. A new binding is needed `
                    + 'only when the organization\'s signing key has changed.');
            } else if (r.status === 'bound') {
                j.notes.push(`${c.operatorId}'s binding carries an older key of ${c.orgCid}; this binding replaces it `
                    + 'with the current key (D6).');
            }
            break;
        }

        case 'bulk_migration_consent': {
            const c = doc.consent;
            requireCurrentEmbedded(c.orgPublicKey);
            const from = requireOpen(c.fromOperatorId, 'the operator whose accounts move');
            requireOpen(c.toOperatorId, 'the target');
            if (from === null) break;
            if (from.status !== 'bound' || from.orgCid !== c.orgCid) {
                j.blocking.push(`Operator ${c.fromOperatorId} is not bound to ${c.orgCid}: a voluntary bulk migration `
                    + `of ${c.fromOperatorId} needs the consent of the organization bound to it.`);
                break;
            }
            await showBinding(from);
            if (!sameBytes(from.orgPublicKey ?? new Uint8Array(), c.orgPublicKey)) {
                j.blocking.push(`${c.fromOperatorId}'s binding carries another key of ${c.orgCid} (D6): sign a `
                    + `binding at sequence ${from.sequence + 1} first, then this consent.`);
            }
            break;
        }

        case 'account_migration_consent': {
            const c = doc.consent;
            requireCurrentEmbedded(c.accountPublicKey);
            requireOpen(c.fromOperatorId, 'the current origin');
            requireOpen(c.toOperatorId, 'the target');
            if (!isOrganizationCid(c.accountCid)) break;
            // An organization's consent is an organization-signed request: under
            // a key a binding of it does not carry, it waits for a new binding.
            for (const code of [...facts.boundCodes].sort()) {
                const r = record(code);
                if (r === null || r.status !== 'bound' || r.orgCid !== c.accountCid) continue;
                await showBinding(r);
                if (!sameBytes(r.orgPublicKey ?? new Uint8Array(), c.accountPublicKey)) {
                    j.blocking.push(`${code}'s binding carries another key of ${c.accountCid} (D6): sign a binding of `
                        + `${code} at sequence ${r.sequence + 1} first, then this consent.`);
                }
            }
            break;
        }
    }
    return j;
}

async function fingerprint(key: Uint8Array): Promise<string> {
    return formatFingerprint(await publicKeyFingerprint(key));
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}
